import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vModelSelect as _vModelSelect, withDirectives as _withDirectives, vModelText as _vModelText, withModifiers as _withModifiers, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container-fluid admin-page" }
const _hoisted_2 = { class: "search-area" }
const _hoisted_3 = { class: "form-group" }
const _hoisted_4 = { class: "form-group" }
const _hoisted_5 = ["value"]
const _hoisted_6 = { class: "form-group" }
const _hoisted_7 = ["value"]
const _hoisted_8 = { class: "form-group" }
const _hoisted_9 = ["value"]
const _hoisted_10 = { class: "form-group" }

import commentApi from 'src/apis/comment';
import userApi from 'src/apis/user';
import { computed, onMounted, reactive, type ComputedRef } from 'vue';
import type { Comment } from 'src/models/comment';
import type { AdminCommentUpdateRequest } from 'src/models/apis/comment/adminCommentRequest';
import type { AdminUser } from 'src/models/user';
import type { AdminListTableField } from 'src/models/adminListTable';
import { useReactiveMasters } from 'src/composables/useMasters';
import { getLocationDispOfKp } from 'src/lib/commentHelper';
import { lineBreakToBR, splitByLineBreak } from 'src/lib/utils';
import { dtFormat } from 'src/lib/dateHelper';
import { getErrorMessages } from 'src/lib/errMsgHelper';
import type { AxiosError } from 'axios';
import CommentEditModal from 'src/components/Admin/Comment/CommentEditModal.vue';

interface CommentSearchParams {
  commentType: string | null;
  userId: number | null;
  dtFrom: Date;
  dtTo: Date;
  content: string | null;
  shareScope: number | null;
}

type CommentDisp = Comment & {
  tsDisp: string;
  contentDisp: string;
};

interface AdminCommentsTableRow extends CommentDisp {
  commentTypeDisp: string;
  shareScopeLabel: string;
  kpInfo?: string;
  angleDisp?: string;
  hasFile: string;
}

interface CommentsSearchState {
  search: CommentSearchParams;
  filteredComments: CommentDisp[];
  users: AdminUser[];
}

interface CommentsEditState {
  showEditModal: boolean;
  editingComment: Comment | null;
  isRequesting: boolean;
  httpErrorMsgs: string[];
  showDestroyModal: boolean;
  destroyingComment: Comment | null;
  showErrorModal: boolean;
}

const destroyModalTitle = '削除';
const destroyModalMsg = 'この付箋を削除してもよろしいですか？';
const errorModalTitle = 'エラー';
const errorModalMsg = '不明なエラーです。ネットワークの状態をご確認いただき再度お試し下さい。';

export default /*@__PURE__*/_defineComponent({
  __name: 'Comments',
  setup(__props) {

const now = new Date();
const initSearchState = (): CommentSearchParams => {
  return {
    commentType: null,
    userId: null,
    dtFrom: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
    dtTo: new Date(now.getFullYear(), now.getMonth(), now.getDate()),
    content: null,
    shareScope: null,
  };
};

const searchState = reactive<CommentsSearchState>({
  search: initSearchState(),
  filteredComments: [],
  users: [],
});
const editState = reactive<CommentsEditState>({
  showEditModal: false,
  editingComment: null,
  isRequesting: false,
  httpErrorMsgs: [],
  showDestroyModal: false,
  destroyingComment: null,
  showErrorModal: false,
});
const { masters, resolveMastersPromise } = useReactiveMasters();

const convComments = (comments: Comment[]): CommentDisp[] => {
  return comments.map<CommentDisp>(e => {
    return {
      ...e,
      tsDisp: dtFormat(e.ts),
      content: splitByLineBreak(e.content).join('\n'),
      contentDisp: lineBreakToBR(e.content),
    };
  });
};
const doSearch = async(): Promise<void> => {
  searchState.filteredComments = [];
  const params = searchState.search;
  const comments = await commentApi.adminIndex({
    dt_from: params.dtFrom,
    dt_to: params.dtTo ? new Date(params.dtTo.valueOf() + 86400 * 1000) : params.dtTo,
    user_id: params.userId,
    share_scope: params.shareScope,
    comment_type: params.commentType,
    content: params.content,
  });
  if (!comments || comments.length === 0) return;

  searchState.filteredComments = convComments(comments);
};

const getCommentTypeDisp = (type: string): string => {
  for (const commentType of masters.comment.commentTypes) {
    if (commentType.commentType === type) {
      return commentType.commentTypeDisp;
    }
  }
  return type;
};
const getShareScopeLabel = (scope: number): string => {
  for (const shareScope of masters.lov.shareScopes) {
    if (shareScope.key === scope) {
      return shareScope.val;
    }
  }
  return scope.toString();
};
const getKpInfo = (kpUid: string | null): string | undefined => {
  if (!kpUid) return;

  const kps = Array.from(masters.kp.kpMap.values())
    .map(e => Array.from(e.values())).flat(2);
  const kpObj = kps.find(e => e.kpUid === kpUid);
  if (!kpObj) return;

  return getLocationDispOfKp(kpObj, masters.kp.roadNameDispMap);
};

const displayData: ComputedRef<AdminCommentsTableRow[]> = computed(() => {
  return searchState.filteredComments.map(comment => {
    return {
      ...comment,
      commentTypeDisp: getCommentTypeDisp(comment.commentType),
      shareScopeLabel: getShareScopeLabel(comment.shareScope),
      kpInfo: getKpInfo(comment.kpUid),
      angleDisp: comment.angle === null || comment.angle === undefined ? '' : `${comment.angle}°`,
      hasFile: comment.commentFiles.length > 0 ? '有' : '',
    };
  });
});

const fields: AdminListTableField<AdminCommentsTableRow>[] = [
  { name: 'tsDisp', label: '最終更新日時' },
  { name: 'commentTypeDisp', label: '種別' },
  { name: 'kpInfo', label: '場所' },
  { name: 'angleDisp', label: '方角' },
  { name: 'shareScopeLabel', label: '公開範囲' },
  { name: 'userDisplayName', label: '作成者' },
  { name: 'contentDisp', label: '内容', type: 'html' },
  { name: 'hasFile', label: '添付' },
];

const edit = (obj: Comment): void => {
  editState.editingComment = obj;
  editState.httpErrorMsgs = [];
  editState.showEditModal = true;
};
const onUpdateComment = async(id: number, payload: AdminCommentUpdateRequest): Promise<void> => {
  editState.isRequesting = true;
  try {
    await commentApi.adminUpdate(id, payload);
    editState.httpErrorMsgs = [];
    editState.showEditModal = false;
    await doSearch();
  } catch (err) {
    editState.httpErrorMsgs = getErrorMessages(err as AxiosError);
  } finally {
    editState.isRequesting = false;
  }
};
const tryDestroy = (obj: Comment): void => {
  editState.destroyingComment = obj;
  editState.showDestroyModal = true;
};
const doDestroy = async(): Promise<void> => {
  if (!editState.destroyingComment) return;

  try {
    await commentApi.adminDestroy(editState.destroyingComment.id);
    editState.showDestroyModal = false;
    await doSearch();
  } catch {
    editState.showDestroyModal = false;
    editState.showErrorModal = true;
  }
};

onMounted(async() => {
  const [, users] = await Promise.all([
    resolveMastersPromise(),
    userApi.adminIndex({}),
  ]);
  searchState.users = users;
  await doSearch();
});


return (_ctx: any,_cache: any) => {
  const _component_MyCalendar = _resolveComponent("MyCalendar")!
  const _component_AdminListTable = _resolveComponent("AdminListTable")!
  const _component_Modal = _resolveComponent("Modal")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _cache[19] || (_cache[19] = _createElementVNode("div", { class: "title-area" }, [
      _createElementVNode("h2", null, "付箋管理")
    ], -1)),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("form", {
        class: "search-form",
        onSubmit: _cache[6] || (_cache[6] = _withModifiers(() => {}, ["prevent"]))
      }, [
        _createElementVNode("div", _hoisted_3, [
          _cache[11] || (_cache[11] = _createElementVNode("label", { class: "form-control-label" }, "期間", -1)),
          _createElementVNode("div", null, [
            _createVNode(_component_MyCalendar, {
              class: "date-from",
              "date-value": searchState.search.dtFrom,
              onDateInput: _cache[0] || (_cache[0] = ($event: any) => (searchState.search.dtFrom = $event))
            }, null, 8, ["date-value"]),
            _cache[10] || (_cache[10] = _createElementVNode("span", { class: "ib waveline ta-center ml4 mr4" }, "〜", -1)),
            _createVNode(_component_MyCalendar, {
              class: "date-to",
              "date-value": searchState.search.dtTo,
              onDateInput: _cache[1] || (_cache[1] = ($event: any) => (searchState.search.dtTo = $event))
            }, null, 8, ["date-value"])
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _cache[13] || (_cache[13] = _createElementVNode("label", { class: "form-control-label group2" }, "種別", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control comment-type",
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((searchState.search.commentType) = $event))
          }, [
            _cache[12] || (_cache[12] = _createElementVNode("option", { value: null }, null, -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(masters).comment.commentTypes, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.commentType,
                value: e.commentType
              }, _toDisplayString(e.commentTypeDisp), 9, _hoisted_5))
            }), 128))
          ], 512), [
            [_vModelSelect, searchState.search.commentType]
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _cache[15] || (_cache[15] = _createElementVNode("label", { class: "form-control-label group2" }, "公開範囲", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control share-scope",
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((searchState.search.shareScope) = $event))
          }, [
            _cache[14] || (_cache[14] = _createElementVNode("option", { value: null }, null, -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(masters).lov.shareScopes, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.key,
                value: e.key
              }, _toDisplayString(e.valAlt), 9, _hoisted_7))
            }), 128))
          ], 512), [
            [_vModelSelect, searchState.search.shareScope]
          ])
        ]),
        _createElementVNode("div", _hoisted_8, [
          _cache[17] || (_cache[17] = _createElementVNode("label", { class: "form-control-label group2" }, "作成者", -1)),
          _withDirectives(_createElementVNode("select", {
            class: "form-control created-by",
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((searchState.search.userId) = $event))
          }, [
            _cache[16] || (_cache[16] = _createElementVNode("option", { value: null }, null, -1)),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(searchState.users, (e) => {
              return (_openBlock(), _createElementBlock("option", {
                key: e.id,
                value: e.id
              }, _toDisplayString(e.displayName), 9, _hoisted_9))
            }), 128))
          ], 512), [
            [_vModelSelect, searchState.search.userId]
          ])
        ]),
        _createElementVNode("div", _hoisted_10, [
          _cache[18] || (_cache[18] = _createElementVNode("label", { class: "form-control-label keyword" }, "キーワード", -1)),
          _withDirectives(_createElementVNode("input", {
            type: "text",
            class: "form-control keyword",
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((searchState.search.content) = $event))
          }, null, 512), [
            [_vModelText, searchState.search.content]
          ])
        ]),
        _createElementVNode("div", { class: "form-group search-btn ta-center" }, [
          _createElementVNode("button", {
            type: "submit",
            class: "btn btn-primary",
            onClick: doSearch
          }, "検索")
        ])
      ], 32)
    ]),
    _createVNode(_component_AdminListTable, {
      "display-data": displayData.value,
      fields: fields,
      onStartEdit: edit,
      onStartDestroy: tryDestroy
    }, null, 8, ["display-data"]),
    (editState.showEditModal && editState.editingComment)
      ? (_openBlock(), _createBlock(CommentEditModal, {
          key: 0,
          "editing-comment": editState.editingComment,
          "is-requesting": editState.isRequesting,
          "http-error-msgs": editState.httpErrorMsgs,
          onUpdate: onUpdateComment,
          onClose: _cache[7] || (_cache[7] = ($event: any) => (editState.showEditModal = false))
        }, null, 8, ["editing-comment", "is-requesting", "http-error-msgs"]))
      : _createCommentVNode("", true),
    (editState.showDestroyModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 1,
          title: destroyModalTitle,
          "show-bottom-dismiss-button": true,
          onClose: doDestroy,
          onDismiss: _cache[8] || (_cache[8] = ($event: any) => (editState.showDestroyModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: destroyModalMsg
              })
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (editState.showErrorModal)
      ? (_openBlock(), _createBlock(_component_Modal, {
          key: 2,
          title: errorModalTitle,
          "show-bottom-dismiss-button": false,
          onClose: _cache[9] || (_cache[9] = ($event: any) => (editState.showErrorModal = false))
        }, {
          body: _withCtx(() => [
            _createElementVNode("div", null, [
              _createElementVNode("p", {
                class: "p2 font-mtx-desc",
                innerHTML: errorModalMsg
              })
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})